import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Stack,
  Checkbox,
  Button,
  Popover
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useTheme } from '@mui/material';
//constants
import { OCPI_ROLES } from 'src/utils/ocpi.constants';
import MuiConfirmationDialog from '../../@material-extend/MuiConfirmationDialog';

import { statusColorMap } from '../constants/ocpi.constant';

const StyledCard = styled(Card)(({ theme, selected }) => ({
  border: `2px solid ${selected ? theme.palette.success.main : 'transparent'}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
  transition: 'all 0.3s ease',
  background: selected ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)' : 'white',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)'
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const LocationCard = ({ 
  location, 
  selected = false,
  onSelect,
  onView, 
  onEdit, 
  onDelete 
}) => {
  const theme = useTheme();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [evsePopoverAnchor, setEvsePopoverAnchor] = useState(null);
  const [showEvsePopover, setShowEvsePopover] = useState(false);
  // Add error boundary for missing location data
  if (!location) {
    return null;
  }

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(location.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting location:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEvsePopoverOpen = (event) => {
    setEvsePopoverAnchor(event.currentTarget);
    setShowEvsePopover(true);
  };

  const handleEvsePopoverClose = () => {
    setEvsePopoverAnchor(null);
    setShowEvsePopover(false);
  };

  const handleEvsePopoverToggle = (event) => {
    if (showEvsePopover) {
      handleEvsePopoverClose();
    } else {
      handleEvsePopoverOpen(event);
    }
  };

  return (
    <>
      <StyledCard selected={selected}>
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with Title and Party ID */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Checkbox
                checked={selected}
                onChange={() => onSelect?.(location.id)}
                color="success"
                size="small"
              />
              <Stack>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2, mb: 0.5 }}>
                  {location.name}
                </Typography>
                {location.address && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {location.address}
                    </Typography>
                  </Stack>
                )}
                {location.address && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {location.city}, {location.countryCode}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Chip
              label={location.partyId}
              size="small"
              variant="outlined"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                minWidth: '60px',
              }}
            />
          </Stack>

          {/* Statistics Section */}
          <Box sx={{ mb: 2, flex: 1 }}>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon icon="eva:charging-fill" width={16} height={16} color="#1976d2" />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {location.evseCount} EVSEs
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon icon="eva:flash-fill" width={16} height={16} color={theme.palette.primary.main} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {location.connectorCount} Connectors
                </Typography>
              </Stack>
            </Stack>

            {/* EVSE Status Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1, fontWeight: 600 }}>
                EVSE Status:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {location.evses.slice(0, 2).map((evse, index) => {
                  return (
                    <Chip
                      key={evse.evseUid}
                      label={`EVSE ${evse.evseUid}: ${evse.status}`}
                      size="small"
                      color={statusColorMap[evse.status].color}
                      variant="filled"
                      sx={{ 
                        fontSize: '0.7rem',
                        height: 20,
                        mb: 0.5,
                        fontWeight: 500,
                        bgcolor: statusColorMap[evse.status].bgcolor,
                        color: statusColorMap[evse.status].text,
                      }}
                    />
                  );
                })}
                {location.evses.length > 2 && (
                  <Chip
                    label={`+${location.evses.length - 2} more`}
                    size="small"
                    variant="outlined"
                    onClick={handleEvsePopoverToggle}
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20,
                      mb: 0.5,
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.light + '20',
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon="eva:eye-fill" width={16} height={16} />}
              onClick={() => onView?.(location)}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                px: 1.5
              }}
            >
              View
            </Button>
            
            {location.role !== OCPI_ROLES.CPO && 
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon="eva:edit-fill" width={16} height={16} />}
              onClick={() => onEdit?.(location)}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                px: 1.5
              }}
            >
              Edit
            </Button>}
            
            {location.role !== OCPI_ROLES.CPO && 
            <IconButton
              size="small"
              color="error"
              onClick={() => setShowDeleteDialog(true)}
              sx={{ 
                ml: 'auto',
                '&:hover': { 
                  backgroundColor: theme.palette.error.light + '20' 
                } 
              }}
            >
              <Icon icon="eva:trash-2-fill" width={16} height={16} />
            </IconButton>}
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Delete Confirmation Dialog */}
      <MuiConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Delete Location"
        severity="error"
        contentText="Are you sure you want to delete this location?"
        okText="Delete Location"
        closeText="Cancel"
        isLoading={isDeleting}
        onOk={handleDelete}
        okButtonProps={{
          color: "error",
          startIcon: <Icon icon="eva:trash-2-fill" />
        }}
        content={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Location Name:</strong> {location.name}<br />
              <strong>Location ID:</strong> {location.id}<br />
              <strong>Party ID:</strong> {location.partyId}<br />
              <strong>EVSE Count:</strong> {location.evseCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will permanently delete the location and all associated EVSE data. This cannot be undone.
            </Typography>
          </Box>
        }
      />

      {/* EVSE Status Popover */}
      <Popover
        open={showEvsePopover}
        anchorEl={evsePopoverAnchor}
        onClose={handleEvsePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 1.5,
            maxWidth: 320,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1, fontWeight: 600 }}>
          All EVSE Status:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {location.evses.map((evse) => (
            <Chip
              key={evse.evseUid}
              label={`EVSE ${evse.evseUid}: ${evse.status}`}
              size="small"
              color={statusColorMap[evse.status].color}
              variant="filled"
              sx={{ 
                fontSize: '0.7rem',
                height: 20,
                mb: 0.5,
                fontWeight: 500,
                bgcolor: statusColorMap[evse.status].bgcolor,
                color: statusColorMap[evse.status].text,
              }}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default LocationCard;
